import { InlineAlertType } from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import { OtpVerificationType } from 'features/otp/constants/OtpVerificationType';
import useResendOtp from 'features/otp/hooks/useResendOtp/useResendOtp';
import useVerifyChallenge from 'features/otp/hooks/useVerifyChallenge/useVerifyChallenge';
import OtpScreen from 'features/otp/OtpScreen';
import {
  ChallengeData,
  ErrorViewType,
  V2ABankAccountForm,
} from 'features/v2a/receiver/helpers/V2A.helpers';
import usePaymentDetails from 'features/v2a/receiver/hooks/usePaymentDetails';
import usePaymentOtp from 'features/v2a/receiver/hooks/usePaymentOtp';
import { V2AReceiverContext } from 'features/v2a/receiver/providers/V2AReceiverContextProvider';
import { jwtDecode } from 'jwt-decode';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import FixedCharacterInput from 'shared/components/vxl/input/FixedCharacterInput/FixedCharacterInput';
import { Variants } from 'shared/components/vxl/input/FixedCharacterInput/FixedCharacterInput.constants';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { getLambdaHeaders } from 'shared/hooks/auth/useLambdaHeaders';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import theme from 'shared/styles/theme';

interface PaymentOTPProps {
  challengeData: ChallengeData;
  setChallengeVerified?: (challengeVerified: boolean) => void;
  formData: V2ABankAccountForm;
}

interface Decoded {
  cur: string;
  iss: string;
  spii: string;
  amt: string;
  rpii: string;
  rme: string;
  rid: string;
  sid: string;
  rcc: string;
  rpl4: string;
  exp: number;
}

const OTP_LENGTH = 6;
const RESEND_TIMEOUT = 60;

const PaymentOTP = ({ formData, challengeData }: PaymentOTPProps) => {
  const router = useRouter();
  const { isFeatureEnabled } = useStatsig();

  const isSharedOtpFlow = isFeatureEnabled(
    FeatureFlagKey.MIGRATE_V2A_OTP as FeatureFlagKey,
  );
  const VERIFY_SCREEN_ERROR_CODES = [3, 9, 11];

  const [isDisabled, setIsDisabled] = useState(false);
  const [timer, setTimer] = useState(0);
  const { paymentDetails } = useContext(V2AReceiverContext);
  const { getVaroSessionId, idToken } =
    useContext<AuthContextProps>(AuthContext);
  const { t: translatedTextOtp } = useTranslation('otp');

  const decoded: Decoded = useMemo(() => {
    return jwtDecode(paymentDetails?.token as string);
  }, [paymentDetails?.token]);

  const [externalId, setExternalId] = useState<string | null>(null);
  const [linkSource, setLinkSource] = useState<string | null>(null);
  const [submitFormLoading, setSubmitFormLoading] = useState<boolean>(false);

  const {
    showLoader,
    challengeVerified,
    handleVerifyChallengeAttribute,
    otpError,
    handleResendOTP,
  } = usePaymentOtp(challengeData);
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'verifyEmailCode',
  });

  const { submitForm, errorResponse } = usePaymentDetails();

  const handleClick = () => {
    setShouldResetCode(true);
    setIsDisabled(true);
    setTimer(RESEND_TIMEOUT);
  };

  useEffect(() => {
    if (!isSharedOtpFlow) {
      let interval: ReturnType<typeof setInterval>;
      if (timer > 0) {
        interval = setInterval(() => {
          setTimer((prevTimer) => prevTimer - 1);
        }, 1000);
      } else if (timer === 0) {
        setIsDisabled(false);
      }
      return () => clearInterval(interval);
    }
  }, [timer, isSharedOtpFlow]);

  useEffect(() => {
    if (challengeVerified) {
      setSubmitFormLoading(true);
      void submitForm(formData);
    }
    //Prevents infinite loop calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeVerified]);

  useEffect(() => {
    if (decoded) {
      const userId = decoded.rid;
      setExternalId(userId);
      const source = decoded.rcc;
      setLinkSource(source?.toLowerCase());
    }
  }, [decoded]);

  useViewDidFocusScreenAnalyticsEvent(
    externalId
      ? {
          key:
            linkSource === 'email'
              ? '/payments-detail/v2aReceiverEmailOtpPage'
              : '/payments-detail/v2aReceiverPhoneOtpPage',
          customAttributes: {
            receiver_id: externalId,
          },
        }
      : null,
  );

  const otpEnteredEvent = usePressableAnalytics({
    analyticsId:
      linkSource === 'email' ? 'email-otp-entered' : 'sms-otp-entered',
    customAttributes: {
      receiver_id: externalId ?? '',
    },
  });

  const otpCompletedEvent = usePressableAnalytics({
    analyticsId:
      linkSource === 'email' ? 'email-otp-completed' : 'sms-otp-completed',
    customAttributes: {
      receiver_id: externalId ?? '',
    },
  });

  const otpSubmitEvent = usePressableAnalytics({
    analyticsId: 'verificationCodeSubmitted',
    customAttributes: {
      receiver_id: externalId ?? '',
      source_name: linkSource === 'email' ? 'email_otp_page' : 'sms_otp_page',
    },
  });

  const [shouldResetCode, setShouldResetCode] = useState(false);

  const { verifyChallenge, isLoading: isVerifyChallengeLoading } =
    useVerifyChallenge();

  const resendOtp = useResendOtp();

  const handleBack = () => {
    router.back();
  };
  useBackHandler(handleBack);

  const handleVerifyOtp = async (code: string) => {
    otpSubmitEvent();
    const result = await verifyChallenge(
      {
        code,
        challengeId: challengeData.challengeId,
      },
      {
        verifyType:
          linkSource === 'email'
            ? OtpVerificationType.EMAIL
            : OtpVerificationType.PHONE,
      },
    );

    if (result.success && result.data?.challengeVerified === 'true') {
      setSubmitFormLoading(true);
      void submitForm(formData);
    }

    return {
      success: result.success,
      errorCode: result.errorCode,
      title: result.title,
      message: result.message,
      data: result.data,
    };
  };

  const onResendOtp = async () => {
    const varoSessionId = await getVaroSessionId();
    const headers = await getLambdaHeaders(idToken, varoSessionId);

    return await resendOtp(
      {
        challengeId: challengeData.challengeId,
      },
      headers,
    );
  };

  const descriptionText =
    linkSource === 'email'
      ? translatedTextOtp('emailVerification.descriptionEmail', {
          email: decoded.rme,
        })
      : translatedTextOtp('phoneVerification.description', {
          phone: decoded.rpl4,
        });

  return (
    <View style={styles.container}>
      {isSharedOtpFlow ? (
        <OtpScreen
          flowKey={OtpFlowKey.V2A_RECEIVER}
          verificationType={
            linkSource === 'email'
              ? OtpVerificationType.EMAIL
              : OtpVerificationType.PHONE
          }
          headerText={translatedText('header')}
          description={descriptionText}
          submitButtonLabel={translatedTextOtp('buttons.submit')}
          invalidCodeText={translatedTextOtp('invalidCode')}
          genericErrorText={translatedTextOtp('genericError')}
          newCodeSentText={translatedTextOtp('newCodeSent')}
          isLoading={Boolean(isVerifyChallengeLoading)}
          screenLevelErrorCodes={VERIFY_SCREEN_ERROR_CODES}
          onVerifyOtp={handleVerifyOtp}
          onResendOtp={onResendOtp}
          onBack={handleBack}
          isCode // Pass isCode prop to indicate this is a V2a flow
        />
      ) : (
        <ScrollView
          keyboardShouldPersistTaps="handled"
          style={styles.scrollContainer}
        >
          <View style={styles.body}>
            <TitleLgText numberOfLines={0}>
              {translatedText('header')}
            </TitleLgText>
            <BodyBaseText numberOfLines={0}>
              {translatedText('description', {
                email: decoded.rme as string,
              })}
            </BodyBaseText>
            <FixedCharacterInput
              variant={Variants.Otp}
              onCodeChanged={(code) => {
                if (code.length === OTP_LENGTH) {
                  otpEnteredEvent();
                }
              }}
              style={styles.fixedCharInput}
              sendOtp={(code) => {
                if (code.length === OTP_LENGTH) {
                  void handleVerifyChallengeAttribute(code);
                  otpCompletedEvent();
                }
              }}
              inputBoxLength={OTP_LENGTH}
              errorMessage={
                errorResponse && errorResponse.type === ErrorViewType.INLINE
                  ? errorResponse.error
                  : otpError
              }
              hasError={false}
              shouldResetInput={shouldResetCode}
              onResetComplete={() => setShouldResetCode(false)}
            />
            <Button
              label={`${translatedText('buttons.resend')}  ${timer ? timer.toString() : ''}`}
              onPress={() => {
                handleClick();
                void handleResendOTP();
              }}
              size="LARGE"
              type="SECONDARY"
              width="FULL"
              isLoading={showLoader}
              isDisabled={isDisabled}
              analyticsId={
                linkSource === 'email'
                  ? 'email-new-otp-requested'
                  : 'sms-new-otp-requested'
              }
              customAttributes={{
                receiver_id: externalId,
              }}
            />
            <InlineAlert
              iconKeyType="INFO"
              titleText={translatedText('alert.title')}
              bodyText={translatedText('alert.body')}
              alertType={InlineAlertType.Info}
            />
          </View>
        </ScrollView>
      )}
      <ScreenTransitionOverlay
        isLoading={showLoader || (submitFormLoading && !errorResponse)}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          errorResponse?.error &&
          errorResponse.type === ErrorViewType.TERMINAL ? (
            <ErrorScreen
              messageHeadline={errorResponse.error}
              screenType={errorResponse?.screenType || ErrorScreenType.Error}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  body: {
    gap: theme.spacing.m,
    marginTop: theme.spacing.m,
  },
  container: {
    height: '100%',
  },
  fixedCharInput: {
    flex: 1,
  },
  scrollContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingHorizontal: theme.spacing.m,
  },
});

export default PaymentOTP;
